import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface DocEntry {
  url: string;
  fileType: string;
  docName: string;
}

interface Props {
  /** Multi-doc mode: array of documents with a tab picker. */
  documents?: DocEntry[];
  /** Single-doc mode (legacy). Ignored when `documents` is supplied. */
  url?: string;
  fileType?: string;
  docName?: string;
  /** Optional title shown in the panel header (multi-doc mode). */
  title?: string;
  onClose: () => void;
}

const isWordType = (ft: string) =>
  ['docx', 'doc', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'].includes(ft);

const isPdfType = (ft: string) =>
  ft === 'application/pdf' || ft.endsWith('.pdf') || ft === 'pdf';

const DocumentViewerPanel: React.FC<Props> = ({ documents, url, fileType, docName, title, onClose }) => {
  const docs: DocEntry[] = documents && documents.length > 0
    ? documents
    : url ? [{ url, fileType: fileType ?? '', docName: docName ?? 'Document' }] : [];

  const [activeIdx, setActiveIdx] = useState(0);
  const [useOfficeViewer, setUseOfficeViewer] = useState(true);
  const [panelWidth, setPanelWidth] = useState(768);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  // Track the last fetched URL so we don't re-fetch on unrelated re-renders
  const fetchedUrlRef = useRef<string | null>(null);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;
    const onMove = (ev: MouseEvent) => {
      const newWidth = Math.min(Math.max(startWidth + (startX - ev.clientX), 360), window.innerWidth - 80);
      setPanelWidth(newWidth);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  const active = docs[activeIdx] ?? docs[0];

  // Fetch PDF as a blob so the iframe gets a same-origin blob: URL,
  // bypassing X-Frame-Options / CSP frame-ancestors on R2 presigned URLs.
  useEffect(() => {
    if (!active) return;
    const isPdf = isPdfType(active.fileType);
    if (!isPdf) {
      // Not a PDF — clear any stale blob URL
      if (pdfBlobUrl) { URL.revokeObjectURL(pdfBlobUrl); setPdfBlobUrl(null); }
      fetchedUrlRef.current = null;
      return;
    }
    if (fetchedUrlRef.current === active.url) return; // already fetched this URL
    fetchedUrlRef.current = active.url;
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setPdfLoading(true);
    fetch(active.url)
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
      })
      .catch(() => {
        // Fall back to direct URL if fetch fails
        setPdfBlobUrl(active.url);
      })
      .finally(() => setPdfLoading(false));
    return () => {
      // Cleanup on unmount only — don't revoke mid-render
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.url, active?.fileType]);
  if (!active) return null;

  const isWord = isWordType(active.fileType);
  const isPdf = isPdfType(active.fileType);
  const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(active.url)}`;
  const multiDoc = docs.length > 1;
  const panelTitle = title ?? (multiDoc ? undefined : active.docName);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />

      {/* Panel */}
      <div
        className="fixed inset-y-0 right-0 z-50 flex flex-col bg-white shadow-2xl"
        style={{ width: Math.min(panelWidth, window.innerWidth) }}
      >
        {/* Resize handle */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary-400/40 transition-colors z-10 group"
          onMouseDown={handleResizeMouseDown}
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-12 w-1.5 bg-gray-300 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="min-w-0">
            {panelTitle && (
              <p className="text-sm font-semibold text-gray-900 truncate">{panelTitle}</p>
            )}
            {!multiDoc && (
              <p className="text-xs text-gray-400 mt-0.5">{active.fileType}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            {isWord && !multiDoc && (
              <button
                onClick={() => setUseOfficeViewer(v => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {useOfficeViewer ? 'Download instead' : 'Preview in browser'}
              </button>
            )}
            <a
              href={active.url}
              download={active.docName}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </a>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Document tab picker (multi-doc only) */}
        {multiDoc && (
          <div className="flex gap-1 px-4 py-2 border-b border-gray-100 bg-gray-50 overflow-x-auto flex-shrink-0">
            {docs.map((d, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { setActiveIdx(i); setUseOfficeViewer(true); }}
                className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  i === activeIdx
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                {d.docName}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 bg-gray-100">
          {isPdf ? (
            pdfLoading || !pdfBlobUrl ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading…</div>
            ) : (
              <iframe src={pdfBlobUrl} title={active.docName} className="w-full h-full border-0" />
            )
          ) : isWord && useOfficeViewer ? (
            <iframe src={officeUrl} title={active.docName} className="w-full h-full border-0" />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-8">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center">
                <ArrowDownTrayIcon className="w-8 h-8 text-blue-500" />
              </div>
              <div>
                <p className="text-gray-700 font-medium">{active.docName}</p>
                <p className="text-sm text-gray-500 mt-1">Download to open in Word.</p>
              </div>
              <a
                href={active.url}
                download={active.docName}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
              >
                Download
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DocumentViewerPanel;
